import React, { useCallback, useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  type EventRsvp,
  type EventRsvpStatus,
  getEventRsvps,
  createEventRsvp,
  updateEventRsvp,
  deleteEventRsvp,
} from '../../api/events';
import { DashboardDataTable } from '../../shared/ui';

interface EventRsvpModalProps {
  show: boolean;
  onHide: () => void;
  eventId: string;
  eventName: string;
}

const STATUS_OPTIONS: EventRsvpStatus[] = ['Going', 'Maybe', 'Cancelled'];

function downloadRsvpsExcel(rsvps: EventRsvp[], eventName: string): void {
  const rows = rsvps.map((r) => ({
    Name: r.name,
    Email: r.email ?? '',
    Phone: r.phone ?? '',
    Status: r.status,
    'Created at': r.createdAt ? new Date(r.createdAt).toLocaleString() : '',
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'RSVPs');
  const safeName = eventName.replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').slice(0, 50) || 'event';
  XLSX.writeFile(wb, `${safeName}-RSVPs.xlsx`);
}

const EventRsvpModal: React.FC<EventRsvpModalProps> = ({ show, onHide, eventId, eventName }) => {
  const [rsvps, setRsvps] = useState<EventRsvp[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<{ name: string; email: string; phone: string; status: EventRsvpStatus }>({
    name: '',
    email: '',
    phone: '',
    status: 'Going',
  });
  const [showCreateModal, setShowCreateModal] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const list = await getEventRsvps(eventId);
      setRsvps(list);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load RSVPs', err);
      setError('Unable to load RSVPs for this event.');
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (show) {
      load();
    }
  }, [show, load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required to add an RSVP');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await createEventRsvp(eventId, {
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        status: form.status,
      });
      setForm({ name: '', email: '', phone: '', status: 'Going' });
      await load();
      setShowCreateModal(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to create RSVP', err);
      setError('Failed to add RSVP. Please check the details and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (rsvp: EventRsvp, status: EventRsvpStatus) => {
    setIsLoading(true);
    setError(null);
    try {
      await updateEventRsvp(eventId, rsvp.id, { status });
      await load();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to update RSVP', err);
      setError('Failed to update RSVP status.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (rsvp: EventRsvp) => {
    if (!window.confirm(`Remove RSVP for ${rsvp.name}?`)) return;
    setIsLoading(true);
    setError(null);
    try {
      await deleteEventRsvpsafe(eventId, rsvp.id);
      await load();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete RSVP', err);
      setError('Failed to delete RSVP.');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteEventRsvpsafe = async (eid: string, rid: string) => {
    try {
      await deleteEventRsvp(eid, rid);
    } catch (err) {
      throw err;
    }
  };

  const goingCount = rsvps.filter((r) => r.status === 'Going').length;
  const maybeCount = rsvps.filter((r) => r.status === 'Maybe').length;

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-white d-flex align-items-center">
            <h5 className="modal-title d-flex align-items-center mb-0">
              <i className="bi bi-people-fill me-2 text-success" />
              <span>RSVPs – {eventName}</span>
            </h5>
            <div className="d-flex align-items-center gap-2 ms-auto">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => downloadRsvpsExcel(rsvps, eventName)}
                disabled={isLoading || rsvps.length === 0}
                title="Download RSVP list as Excel"
              >
                <i className="bi bi-file-earmark-excel me-1" />
                Download Excel
              </button>
              <button
                type="button"
                className="btn btn-outline-success btn-sm"
                onClick={() => {
                  setForm({ name: '', email: '', phone: '', status: 'Going' });
                  setError(null);
                  setShowCreateModal(true);
                }}
                disabled={isLoading}
              >
                <i className="bi bi-person-plus me-1" />
                Add RSVP
              </button>
              <button type="button" className="btn-close" onClick={onHide} disabled={isLoading} />
            </div>
          </div>
          <div className="modal-body">
            {error && (
              <div className="alert alert-danger" role="alert">
                <i className="bi bi-exclamation-triangle me-2" />
                {error}
              </div>
            )}

            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <span className="badge bg-success">
                <i className="bi bi-person-check me-1" />
                Going: {goingCount}
              </span>
              <span className="badge bg-warning text-dark">
                <i className="bi bi-question-circle me-1" />
                Maybe: {maybeCount}
              </span>
              <span className="badge bg-secondary">
                <i className="bi bi-people me-1" />
                Total RSVPs: {rsvps.length}
              </span>
            </div>

            {isLoading && (
              <div className="text-center py-2">
                <div className="spinner-border spinner-border-sm text-primary" role="status" />
              </div>
            )}

            {rsvps.length === 0 ? (
              <p className="text-muted mb-0">No RSVPs recorded for this event yet.</p>
            ) : (
              <DashboardDataTable size="sm" aria-label="Event RSVPs">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                    {rsvps.map((r) => (
                      <tr key={r.id}>
                        <td>{r.name}</td>
                        <td className="text-muted small">
                          {r.email && <div><i className="bi bi-envelope me-1" />{r.email}</div>}
                          {r.phone && <div><i className="bi bi-telephone me-1" />{r.phone}</div>}
                          {!r.email && !r.phone && <span className="text-muted">No contact details</span>}
                        </td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={r.status}
                            onChange={(e) => handleStatusChange(r, e.target.value as EventRsvpStatus)}
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDelete(r)}
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </DashboardDataTable>
            )}
          </div>
        </div>
      </div>

      {showCreateModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-person-plus me-2" />
                  Add RSVP
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCreateModal(false);
                    setError(null);
                  }}
                  disabled={isLoading}
                />
              </div>
              <form onSubmit={handleCreate}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.name}
                      onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={form.email}
                      onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={form.phone}
                      onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={form.status}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, status: e.target.value as EventRsvpStatus }))
                      }
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setShowCreateModal(false);
                      setError(null);
                    }}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isLoading}>
                    <i className="bi bi-plus-circle me-2" />
                    Add RSVP
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventRsvpModal;

